import {
  afterNextRender,
  Component, signal
} from '@angular/core';
import { IndustryBlockComponent, IndustryBlockConfig } from '../industry-block/industry-block.component';
import Swiper from 'swiper';
import { Autoplay, EffectFade } from 'swiper/modules';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-industries',
  imports: [
    IndustryBlockComponent,
    TranslateModule
  ],
  templateUrl: './industries.component.html',
  styleUrl: './industries.component.scss'
})
export class IndustriesComponent {
  activeSlideIndex = signal(0);
  private swiperInstance: Swiper | null = null;

  industries: IndustryBlockConfig[] = [
    {
      id: 1,
      label: 'INDUSTRIES.RETAIL.LABEL',
      title: 'INDUSTRIES.RETAIL.TITLE',
      text: 'INDUSTRIES.RETAIL.TEXT',
      buttonText: 'COMMON.LEARN_MORE',
      buttonLink: '/bransje/retail',
      image: {
        src: '/assets/img/website/industries/retail.png',
        alt: 'Retail team working together'
      },
      logos: [
        {
          src: '/assets/images/logos/kaffebrenneriet.png',
          alt: 'Kaffebrenneriet logo',
          width: 154,
          height: 28,
        },
        {
          src: '/assets/img/website/retail/devold.png',
          alt: 'Devold logo',
          width: 101,
          height: 24,
        },
        {
          src: '/assets/img/website/retail/eplehuset.png',
          width: 64,
          height: 46,
          alt: 'Eplehuset logo'
        },
        {
          src: '/assets/img/website/retail/jernia.png',
          width: 102,
          height: 24,
          alt: 'Jernia logo'
        }
      ],
      backgroundColor: '#F5EBFF'
    },
    {
      id: 2,
      label: 'INDUSTRIES.OFFSHORE.LABEL',
      title: 'INDUSTRIES.OFFSHORE.TITLE',
      text: 'INDUSTRIES.OFFSHORE.TEXT',
      buttonText: 'COMMON.LEARN_MORE',
      buttonLink: '/bransje/offshore',
      image: {
        src: '/assets/img/website/industries/offshore.png',
        alt: 'Offshore team working together'
      },
      logos: [
        {
          src: '/assets/img/website/offshore/logo/torvald.png',
          width: 106,
          height: 35,
          alt: 'Torvald logo'
        },
        {
          src: '/assets/images/logos/solstad_offshore.png',
          width: 123,
          height: 38,
          alt: 'Solstad Offshore logo'
        },
        {
          src: '/assets/img/website/offshore/logo/sea1.png',
          width: 77,
          height: 20,
          alt: 'Sea1 logo'
        },
        {
          src: '/assets/img/website/offshore/logo/westcon.png',
          width: 129,
          height: 20,
          alt: 'Westcon logo'
        }
      ],
      backgroundColor: '#FEFBDA'
    },
    {
      id: 3,
      label: 'INDUSTRIES.LAW.LABEL',
      title: 'INDUSTRIES.LAW.TITLE',
      text: 'INDUSTRIES.LAW.TEXT',
      buttonText: 'COMMON.LEARN_MORE',
      buttonLink: '/bransje/advokathus',
      image: {
        src: '/assets/img/website/industries/law.png',
        alt: 'Law illustration'
      },
      logos: [
        {
          src: '/assets/img/lawyer/haavind.png',
          width: 102,
          height: 23,
          alt: 'Haavind logo'
        },
        {
          src: '/assets/images/logos/arntzen_grette.png',
          width: 211,
          height: 14,
          alt: 'Arntzenn Grette logo'
        },
        {
          src: '/assets/images/logos/wikborg_rein.png',
          width: 192,
          height: 20,
          alt: 'Wikborg Rein logo'
        }
      ],
      backgroundColor: '#F5EBFF'
    },
    {
      id: 4,
      label: 'INDUSTRIES.GROUP_STRUCTURE.LABEL',
      title: 'INDUSTRIES.GROUP_STRUCTURE.TITLE',
      text: 'INDUSTRIES.GROUP_STRUCTURE.TEXT',
      buttonText: 'COMMON.LEARN_MORE',
      buttonLink: '/bransje/konsern',
      image: {
        src: '/assets/img/website/industries/group_structure.png',
        alt: 'Company group structure illustration'
      },
      logos: [
        {
          src: 'assets/img/website/group-structure/fred_plsen.png',
          width: 196,
          height: 20,
          alt: 'Fred Olsen logo'
        },
        {
          src: 'assets/img/website/group-structure/ikm_logo.png',
          width: 71,
          height: 64,
          alt: 'IKM logo'
        },
        {
          src: 'assets/img/website/group-structure/bransdal.png',
          width: 212,
          height: 31,
          alt: 'Bransdal logo'
        }
      ],
      backgroundColor: '#FEFBDA'
    },
    {
      id: 5,
      label: 'INDUSTRIES.CONSTRUCTION.LABEL',
      title: 'INDUSTRIES.CONSTRUCTION.TITLE',
      text: 'INDUSTRIES.CONSTRUCTION.TEXT',
      buttonText: 'COMMON.LEARN_MORE',
      buttonLink: '/bransje/bygg-anlegg',
      image: {
        src: '/assets/img/website/industries/construction.png',
        alt: 'Construction industry illustration'
      },
      logos: [
        {
          src: '/assets/images/logos/lett_tak.png',
          width: 109,
          height: 28,
          alt: 'Lett-Tak logo'
        },
        {
          src: '/assets/images/logos/maskin.png',
          width: 131,
          height: 32,
          alt: 'Maskin logo'
        },
        {
          src: '/assets/images/logos/aase.png',
          width: 67,
          height: 36,
          alt: 'Aase logo'
        },
        {
          src: '/assets/images/logos/helgevold.png',
          width: 152,
          height: 15,
          alt: 'Helgevold logo'
        }
      ],
      backgroundColor: '#FEFBDA'
    }
  ];

  constructor() {
    afterNextRender(() => {
      this.initializeSwiper();
    });
  }

  private initializeSwiper(): void {
    try {
      this.swiperInstance = new Swiper('.industries-carousel', {
        modules: [Autoplay, EffectFade],
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        resizeObserver: false,
        autoplay: {
          delay: 5000,
          disableOnInteraction: true,
          pauseOnMouseEnter: false,
        },
        speed: 800,
        loop: true,
        allowTouchMove: false,
        on: {
          slideChange: (swiper) => {
            this.activeSlideIndex.set(swiper.realIndex);
          }
        }
      });
    } catch (error) {
      console.error('Error initializing Swiper carousel:', error);
    }
  }

  goToSlide(index: number): void {
    if (this.swiperInstance) {
      this.swiperInstance.slideToLoop(index);
    }
  }
}
